import { Component, OnInit, QueryList, ViewChildren } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";

import { Observable } from "rxjs";
import { first, shareReplay } from "rxjs/operators";

import { markAsTouched, updateValueAndValidity } from "@dffedb/forms";
import { DeviceService } from "@dffedb/util";
import { AktuelInstallationKeyObserver } from "@e-forsyning/common/installation";
import { RedirectService } from "@e-forsyning/common/router";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

import { IFlyttemeddelelse } from "../model/flyttemeddelelse.model";
import { FlyttemeddelelseViewModel } from "../model/flyttemeddelelse.view-model";
import { FlyttemeddelelseKvitteringMailService } from "../shared/flyttemeddelelse-kvittering-mail.service";
import { FlyttemeddelelseSandbox } from "../shared/flyttemeddelelse.sandbox";
import { MatFormField } from "@angular/material/form-field";

@Component({
    styleUrls: ["./flyttemeddelelse.component.scss"],
    templateUrl: "./flyttemeddelelse.component.html"
})
export class FlyttemeddelelseComponent implements OnInit {
    public erSpinnerSynlig = false;
    public erMobilVisning = false;
    public vm$: Observable<FlyttemeddelelseViewModel>;
    public formGroupErrors: { label: string; error: string; inputId: string }[] = [];

    constructor(
        private readonly sandbox: FlyttemeddelelseSandbox,
        private readonly kvitteringMailService: FlyttemeddelelseKvitteringMailService,
        private readonly redirectService: RedirectService,
        private readonly aktuelInstallationKeyObserver: AktuelInstallationKeyObserver,
        private readonly umbracoIndstillingerObserver: UmbracoIndstillingerObserver,
        private readonly deviceService: DeviceService
    ) {}

    public ngOnInit(): void {
        this.erMobilVisning = this.deviceService.erMobilVisning();
        this.vm$ = this.sandbox.hentViewModel().pipe(shareReplay(1));
    }

    public async opretFlyttemeddelelse(formGroup: UntypedFormGroup): Promise<void> {
        markAsTouched(formGroup);
        updateValueAndValidity(formGroup);

        if (!formGroup.valid) {
            // timeout for at sikre at fejlmeddelelserne er blevet opdateret i DOM'en
            setTimeout(() => {
                this.formGroupErrors = this.getErrors();
                document.scrollingElement.scrollTop = 0;
            }, 250);
            return;
        }

        this.erSpinnerSynlig = true;
        const installation = await this.aktuelInstallationKeyObserver.valueChanges.pipe(first()).toPromise();
        const flyttemeddelelse: IFlyttemeddelelse = {
            ...formGroup.getRawValue(),
            installation
        };
        await this.sandbox.opretFlyttemeddelelse(flyttemeddelelse).toPromise();
        await this.sendFlyttekvitteringMail(flyttemeddelelse);
        this.redirectService.redirectTilFlyttekvittering(flyttemeddelelse);
        // Vi skjuler ikke spinneren igen, da vi gerne vil vise den indtil vi har redirected til kvitteringssiden.
    }

    public getErrors() {
        const formFields = document.querySelectorAll("mat-form-field.mat-form-field-invalid");
        const errors: { label: string; error: string; inputId: string }[] = [];

        formFields.forEach((field) => {
            const label = field.querySelector(".mat-form-field-label")?.textContent.replace("*", "-");
            const error = field.querySelector(".mat-error")?.textContent;
            const inputId = field.querySelector("input").id;
            errors.push({ label, error, inputId });
        });

        return errors;
    }

    public navigationToError(id: string) {
        if (!id) return;
        const element = document.getElementById(id);
        element.focus();
        element.scrollIntoView({ behavior: "smooth" });
    }

    private async sendFlyttekvitteringMail(flyttemeddelelse: IFlyttemeddelelse): Promise<void> {
        const email = (await this.umbracoIndstillingerObserver.valueChanges.pipe(first()).toPromise()).aktuelEforsyning.firmaInfo.email;
        return await this.kvitteringMailService.sendMail(flyttemeddelelse, email);
    }
}
